import "./CMSCustomers.css";

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import AddandEditCustomer from "../AddandEditCustomer/AddandEditCustomer";
import { CMSCustomer } from "../../../types/CMSTypes";
import CMSCustomerDetailsModal from "./CMSCustomerDetailsModal";
import { CMSService } from "../../../services/CMSService";
import { SearchService } from "../../../services/searchService";
import StoryTabs from "../../AdminConsole/AdminConsoleContent/stories/components/StoryTabs";
import { epochToDateStringWithYear } from "../../../helper";
import { useLoader } from "../../../hooks/useLoader";
import { usePagination } from "../../../hooks/usePagination";

type Props = {
  allActiveCustomer: { customers: CMSCustomer[]; hasMore: boolean } | null;
  allInctiveCustomer: { customers: CMSCustomer[]; hasMore: boolean } | null;
  initialCalls: Function;
  isAdminUser: boolean;
  getAllActiveCustomersFromServer: Function;
  getAllInctiveCustomersFromServer: Function;
};

const TABS = [
  {
    title: "Active Customers",
    value: "active_customers",
  },
  {
    title: "Inactive Customers",
    value: "inactive_customers",
  },
];

const CMSCustomers: React.FC<Props> = ({
  allActiveCustomer,
  allInctiveCustomer,
  initialCalls,
  isAdminUser,
  getAllActiveCustomersFromServer,
  getAllInctiveCustomersFromServer,
}) => {
  const [selectedtab, setSelectedTab] = useState(TABS[0].value);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [showCustomerDetailsModal, setShowCustomerDetailsModal] =
    useState<boolean>(false);
  const selectedCustomer = useRef<CMSCustomer | null>(null);
  const [searchString, setSearchString] = useState("");
  const [searchResults, setSearchResults] = useState<CMSCustomer[] | null>(
    null
  );
  const [isSearching, setIsSearching] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout>();

  const isActiveTabSelected = selectedtab === TABS[0].value;

  const cbFunction = isActiveTabSelected
    ? getAllActiveCustomersFromServer
    : getAllInctiveCustomersFromServer;

  const hasMore = isActiveTabSelected
    ? !!allActiveCustomer?.hasMore
    : !!allInctiveCustomer?.hasMore;

  usePagination(hasMore, cbFunction);

  const performSearch = useCallback(async (searchTerm: string) => {
    try {
      setIsSearching(true);
      const response = await SearchService.searchCustomerByName({
        name: searchTerm,
      });
      setSearchResults(response?.data?.customers || []);
    } catch (error) {
      console.error("Search failed:", error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Effect to trigger search when searchString changes
  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (searchString.trim()) {
      timeoutRef.current = setTimeout(() => {
        performSearch(searchString);
      }, 500);
    } else {
      setSearchResults(null);
      setIsSearching(false);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [searchString, performSearch]);

  const visibleData = useMemo(() => {
    if (searchString.trim()) {
      return searchResults || [];
    }
    return selectedtab === TABS[0].value
      ? allActiveCustomer?.customers
      : allInctiveCustomer?.customers;
  }, [
    allActiveCustomer,
    allInctiveCustomer,
    selectedtab,
    searchString,
    searchResults,
  ]);

  const { showLoading } = useLoader();

  const addNewCustomerCall = async (customerData: CMSCustomer) => {
    try {
      showLoading(true);
      const response = await CMSService.createCustomer(customerData);
      console.log(response, "response");
      showLoading(false);
      handleCloseModal();
    } catch (error) {
      showLoading(false);
      if (error.response.data.message) {
        alert(error.response.data.message);
      } else {
        alert("Something went wrong");
      }
      console.log(error);
    }
  };

  const editCustomerCall = async (customerData: CMSCustomer) => {
    try {
      showLoading(true);
      const response = await CMSService.editCustomer(customerData);
      showLoading(false);
      handleCloseModal();
    } catch (error) {
      showLoading(false);
      console.log(error);
    }
  };

  const handleSaveCustomer = (customerData: Partial<CMSCustomer>) => {
    if (customerData.id) {
      editCustomerCall(customerData as CMSCustomer);
    } else {
      addNewCustomerCall(customerData as CMSCustomer);
    }
  };

  const handleCloseModal = () => {
    selectedCustomer.current = null;
    setIsModalOpen(false);
    initialCalls();
  };

  const handleCloseDeatilsModal = () => {
    selectedCustomer.current = null;
    setShowCustomerDetailsModal(false);
  };

  const handleDeactivate = async (customerId: string) => {
    try {
      showLoading(true);
      const response = await CMSService.deactivateCustomerById({
        customerId,
      });
      alert("Customer has been deactivated successfully");
      showLoading(false);
      initialCalls();
      console.log(response);
    } catch (error) {
      showLoading(false);
      if (error.response.data.message) {
        alert(error.response.data.message);
      } else {
        alert("Something went wrong");
      }
      console.log(error);
    }
  };

  const handleAddNewCustomer = () => {
    selectedCustomer.current = null;
    setIsModalOpen(true);
  };

  const handleEdit = (customer: CMSCustomer) => {
    selectedCustomer.current = customer;
    setIsModalOpen(true);
  };

  const handleShowDetailsClick = (customer: CMSCustomer) => {
    selectedCustomer.current = customer;
    setShowCustomerDetailsModal(true);
  };

  const handleCopy = (referralCode: string) => {
    navigator.clipboard
      .writeText(referralCode)
      .then(() => {
        alert("Text copied to clipboard!");
      })
      .catch((err) => {
        alert("Failed to copy text: " + err);
      });
  };

  const handleOnChangeTextOnSearch = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { value } = e.target;
    setSearchString(value);
  };

  return (
    <div className="cmscustomers-main">
      <div className="header-container">
        <div style={{ fontSize: 24, fontWeight: 700, color: "#161616" }}>
          {isAdminUser ? "Customers" : "Plan Info"}
        </div>
        {isAdminUser && (
          <button
            className="add-customer-button"
            onClick={handleAddNewCustomer}
          >
            Add New Customer
          </button>
        )}
      </div>

      {isAdminUser && (
        <StoryTabs
          tabs={TABS}
          selectedtab={selectedtab}
          setSelectedTab={setSelectedTab}
        />
      )}
      {isAdminUser && (
        <div className="top-row-customers">
          <span className="delivery-info">Delivery Information</span>
          <div className="header-right">
            <span className="search-container">
              <button className="search-button">
                <img
                  src="./assets/searchcms.svg"
                  alt="Search"
                  className="search-icon"
                />
                <input
                  type="text"
                  placeholder="Search customers by name, institution, or country..."
                  className="search-input"
                  style={{
                    backgroundColor: "white",
                    border: "none",
                    borderRadius: "none",
                    display: "flex",
                  }}
                  value={searchString}
                  onChange={handleOnChangeTextOnSearch}
                />
                {isSearching && (
                  <div
                    style={{
                      marginLeft: "8px",
                      fontSize: "12px",
                      color: "#666",
                    }}
                  >
                    Searching...
                  </div>
                )}
              </button>
            </span>
          </div>
        </div>
      )}
      <div>
        <table className="report-table-cms">
          <thead>
            <tr className="report-table-heading">
              <th>
                <div style={{ display: "flex", alignItems: "center" }}>
                  Customer Name
                  <img
                    src="./assets/arrowdown.svg"
                    alt="arrowDown"
                    style={{
                      marginLeft: 5,
                    }}
                  />
                </div>
              </th>
              <th>
                <div style={{ display: "flex", alignItems: "center" }}>
                  Country
                  <img
                    src="./assets/arrowdown.svg"
                    alt="arrowDown"
                    style={{
                      marginLeft: 5,
                    }}
                  />
                </div>
              </th>
              <th>Account Type</th>
              <th>Referral Code</th>
              <th>Institute</th>
              <th>No of Users</th>
              <th>Max Users</th>
              <th>Price</th>
              <th>Renewal Date</th>
              {isAdminUser && <th>Actions</th>}
            </tr>
          </thead>
          <tbody className="report-table-body">
            {visibleData && visibleData.length > 0 ? (
              visibleData.map((row, index) => (
                <tr key={index}>
                  <td
                    onClick={() => isAdminUser && handleShowDetailsClick(row)}
                    style={{
                      cursor: "pointer",
                      width: "300px",
                      maxWidth: "300px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {row.name}
                  </td>
                  <td
                    style={{
                      color: "#667085",
                      fontSize: 14,
                      fontWeight: 400,
                    }}
                  >
                    {row.country || "country"}
                  </td>
                  <td
                    style={{
                      color: "#667085",
                      fontSize: 14,
                      fontWeight: 400,
                    }}
                  >
                    {row.subscriptionType}
                  </td>
                  <td
                    style={{
                      color: "#667085",
                      fontSize: 14,
                      fontWeight: 400,
                    }}
                  >
                    {row.id ?? row.customerId ?? ""}
                  </td>
                  <td
                    style={{
                      color: "#667085",
                      fontSize: 14,
                      fontWeight: 400,
                    }}
                  >
                    {row.institutionName}
                  </td>
                  <td
                    style={{
                      color: "#667085",
                      fontSize: 14,
                      fontWeight: 400,
                    }}
                  >
                    {row.currentUsers ?? 0}
                  </td>
                  <td
                    style={{
                      color: "#667085",
                      fontSize: 14,
                      fontWeight: 400,
                    }}
                  >
                    {row.numberOfUsers}
                  </td>
                  <td
                    style={{
                      color: "#667085",
                      fontSize: 14,
                      fontWeight: 400,
                    }}
                  >
                    €{row.price}
                  </td>
                  <td
                    style={{
                      color: "#667085",
                      fontSize: 14,
                      fontWeight: 400,
                    }}
                  >
                    {epochToDateStringWithYear(row.endDate._seconds, "short")}
                  </td>
                  {isAdminUser && (
                    <td
                      style={{
                        color: "#667085",
                        fontSize: 14,
                        fontWeight: 400,
                      }}
                    >
                      <div
                        className="actions-container"
                        style={{ display: "flex", flexDirection: "row" }}
                      >
                        <img
                          src="./assets/Edit.svg"
                          alt="Edit"
                          className="action-icon"
                          onClick={() => handleEdit(row)}
                        />
                      </div>
                    </td>
                  )}
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={isAdminUser ? 9 : 8}
                  style={{
                    textAlign: "center",
                    padding: "20px",
                    color: "#667085",
                  }}
                >
                  {searchString.trim()
                    ? isSearching
                      ? "Searching..."
                      : "No customers found matching your search."
                    : "No customers available."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {isModalOpen && (
        <AddandEditCustomer
          onClose={handleCloseModal}
          onSave={handleSaveCustomer}
          onDeactivate={handleDeactivate}
          customer={selectedCustomer.current}
        />
      )}
      {showCustomerDetailsModal && (
        <CMSCustomerDetailsModal
          onClose={handleCloseDeatilsModal}
          customer={selectedCustomer.current}
        />
      )}
    </div>
  );
};

export default CMSCustomers;
